import React, { useState, useEffect } from 'react';
import styles from '../styles/Toast.module.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCheckCircle, 
  faExclamationCircle, 
  faInfoCircle, 
  faTimesCircle 
} from "@fortawesome/free-solid-svg-icons";

/**
 * Toast notification component
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Type of toast: 'success', 'error', 'info', or 'warning'
 * @param {string} props.message - Message to display
 * @param {number} props.duration - Duration in milliseconds before auto-dismissing
 * @param {Function} props.onClose - Function to call when toast is closed
 * @returns {JSX.Element}
 */
const Toast = ({ type = 'info', message, duration = 3000, onClose }) => {
  const [visible, setVisible] = useState(true);
  
  // Choose icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return faCheckCircle;
      case 'error':
        return faTimesCircle;
      case 'warning':
        return faExclamationCircle;
      default:
        return faInfoCircle;
    }
  };
  
  useEffect(() => {
    // Auto-dismiss the toast after specified duration
    const timer = setTimeout(() => {
      setVisible(false);
      // Add a short delay to allow exit animation
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    }, duration);
    
    return () => {
      clearTimeout(timer);
    };
  }, [duration, onClose]);
  
  const handleClose = () => {
    setVisible(false);
    // Add a short delay to allow exit animation
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };
  
  return (
    <div 
      className={`${styles.toast} ${styles[type]} ${visible ? styles.visible : styles.hidden}`}
      role="alert"
    >
      <div className={styles.iconContainer}>
        <FontAwesomeIcon icon={getIcon()} />
      </div>
      <div className={styles.content}>
        {message}
      </div>
      <button 
        className={styles.closeButton} 
        onClick={handleClose}
        aria-label="Close notification"
      >
        <FontAwesomeIcon icon={faTimesCircle} />
      </button>
      <div className={styles.progress} style={{ animationDuration: `${duration}ms` }} />
    </div>
  );
};

export default Toast;
